import { getDatePartsWithDefaults, isApproximateDate, getChurchStandardDateFormatWithParts } from './factDate_Helper'
import supportedCountries from '../../wcf/AppWcf/components/Heritage/helpers/countryObjects'
import { getGenderedIconUrl } from '../../wcf/global/base64Icons'

export function getChildAncestorLL(workingTree, atn, locationLookup) {
  const isMale = atn % 2 === 0
  let childDPLL
  let childFact
  const newAn = isMale ? atn / 2 : (atn - 1) / 2

  for (let i = 0; i < workingTree.length; i++) {
    if (newAn === parseInt(workingTree[i].atn, 10)) {
      if (workingTree[i].facts) {
        childFact = getValidLocationFact(workingTree[i].facts, locationLookup)
      }

      if (childFact != null) {
        // We aren't going to trust a 0 0 lat long
        const placeId = childFact.placeDescription
        const childLocation = locationLookup[placeId]

        if (childLocation) {
          childLocation.placeId = placeId
        }

        if (childLocation && childLocation.latitude !== 0 && childLocation.longitude !== 0) {
          childDPLL = childLocation
          break
        }
      }
    }
  }

  return {
    ll: childDPLL,
    an: newAn,
  }
}

const getNormalizedPlace = (name) => {
  const parts = name ? name.split(', ') : []
  const len = parts && parts.length ? parts.length : 0
  return len > 3 ? `${parts[0]}, ${parts.slice(len - 2, len - 1)}, ${parts.slice(len - 1, len)}` : parts.join(', ')
}

export function expandFact(fact, locationLookup) {
  const matchingFact = fact
  if (matchingFact && matchingFact.date) {
    const dateParts = getDatePartsWithDefaults(matchingFact.date)

    if (dateParts) {
      const placeId = matchingFact.placeDescription ? matchingFact.placeDescription : null
      matchingFact.display = {
        type: matchingFact.type,
        date: {
          isApproximate: isApproximateDate(matchingFact.date),
          year: dateParts.year,
          month: dateParts.month,
          day: dateParts.day,
          normalized: getChurchStandardDateFormatWithParts({
            year: dateParts.year,
            month: dateParts.month,
            day: dateParts.day,
          }),
        },
        place: placeId && locationLookup[placeId]?.name ? getNormalizedPlace(locationLookup[placeId].name) : '',
        placeFull: placeId && locationLookup[placeId]?.name ? locationLookup[placeId].name : '',
      }

      matchingFact.latLong =
        placeId && locationLookup[placeId]
          ? {
              latitude: parseInt(locationLookup[placeId].latitude, 10),
              longitude: parseInt(locationLookup[placeId].longitude, 10),
            }
          : {
              latitude: 0,
              longitude: 0,
            }
    }
  }

  return matchingFact
}

export function expandPerson({ person, locationLookup, tree }) {
  const gender = person.gender.replace('http://gedcomx.org/', '').toLowerCase()
  const expandedFacts = []
  const expandedPerson = {
    ...person,
    id: person.personId,
    gender,
    atn: parseInt(person.atn, 10),
    defaultIconUrl: getGenderedIconUrl(gender),
    genderIconUrl: getGenderedIconUrl(gender, false),
  }
  if (person && person.facts && person.facts.length > 0) {
    person.facts.forEach((fact) => {
      expandedFacts.push(expandFact(fact, locationLookup))
    })
  }
  expandedPerson.facts = expandedFacts
  expandedPerson.latLong = getPersonLatLong(tree, expandedPerson, locationLookup)

  return expandedPerson
}

export function expandFacts(facts, locationLookup) {
  const expandedFacts = []
  if (facts && facts.length > 0) {
    facts.forEach((fact) => {
      expandedFacts.push(expandFact(fact, locationLookup))
    })
  }
  return expandedFacts
}

export function getValidLocationFact(facts, locationLookup) {
  return facts
    ?.filter(({ placeDescription }) => placeDescription)
    .find(({ placeDescription }) => {
      const location = locationLookup[placeDescription]
      return !!(location?.latitude && location.longitude)
    })
}

export function checkMissingLocation(person) {
  const birthPlace = {
    place: '',
    placeFull: '',
  }
  if (person && person.facts && person.facts.length !== 0) {
    const birthInfo = person.facts.find((fact) => fact.type === 'http://gedcomx.org/Birth')
    if (birthInfo) {
      birthPlace.place = birthInfo?.display?.place
      birthPlace.placeFull = birthInfo?.display?.placeFull
    }
  }
  return birthPlace
}

export function getPersonLatLong(workingTree, person, locationLookup) {
  let foundLocation
  let locationFact
  if (person && person.facts && person.facts.length > 0) {
    locationFact = getValidLocationFact(person.facts, locationLookup)
  }

  if (!locationFact) {
    foundLocation = getNearestDescendantsValidLatLong(workingTree, person.atn, locationLookup)
  } else {
    const placeId = locationFact.placeDescription
    foundLocation = locationLookup[placeId]

    if (!foundLocation) {
      foundLocation = getNearestDescendantsValidLatLong(workingTree, person.atn, locationLookup)
    }
    foundLocation.placeId = placeId
  }
  foundLocation.latitude = parseFloat(foundLocation.latitude)
  foundLocation.longitude = parseFloat(foundLocation.longitude)

  if (!foundLocation.latitude || !foundLocation.longitude) {
    foundLocation.latitude = 0
    foundLocation.longitude = 0
  }
  return foundLocation
}

export function getAncestorCountryName(person, ancestralCountries) {
  const matchedLocation = ancestralCountries.find((country) => country.placeId === person.latLong.placeId)
  const countryName = matchedLocation ? matchedLocation.countryName : ''
  return countryName
}

export function getAncestorCountryCode(person, ancestralCountries) {
  const matchedLocation = ancestralCountries.find((country) => country.placeId === person.latLong.placeId)
  const returnCode = matchedLocation ? matchedLocation.countryCode : ''
  return returnCode
}

export function getCurrentCountryCodes(person, ancestralCountries) {
  const matchedLocation = ancestralCountries.find((country) => country.placeId === person.latLong.placeId)
  const countryName =
    matchedLocation && matchedLocation.currentCountryCodes && matchedLocation.countryName ? matchedLocation : ''
  return countryName
}

export function getPlaceIdCountryCodes({ placeIds, locationLookup }) {
  const matchedLocations = []
  if (placeIds && placeIds.length > 0 && locationLookup) {
    placeIds.forEach((placeId) => {
      if (locationLookup[placeId] && locationLookup[placeId].countryCode) {
        matchedLocations.push(locationLookup[placeId].countryCode)
      }
    })
  }

  return matchedLocations
}

export function getAncestryPercentages(tree, ancestralCountries) {
  let curCountryCode = ''
  let foundCountryInAddedPercentages
  let perc
  let currentCountryCodes
  let curCountryName
  const percentageRelatedArr = []

  tree.forEach((person) => {
    curCountryName = getAncestorCountryName(person, ancestralCountries)
    curCountryCode = getAncestorCountryCode(person, ancestralCountries)
    currentCountryCodes = getCurrentCountryCodes(person, ancestralCountries)
    if (curCountryCode === '') {
      // console.log(`${person.name} doesn't have a valid country code`) // eslint-disable-line
    }

    foundCountryInAddedPercentages = false

    for (let j = 0; j < percentageRelatedArr.length; j++) {
      const percentageCountry = percentageRelatedArr[j]
      if (
        percentageCountry.cc === curCountryCode ||
        (percentageCountry?.countryName && percentageCountry.countryName === curCountryName)
      ) {
        percentageCountry.val++
        foundCountryInAddedPercentages = true
        break
      }
    }

    if (!foundCountryInAddedPercentages && curCountryCode !== '') {
      // Don't include just any country!
      if (currentCountryCodes) {
        percentageRelatedArr.push({
          cc: 'none',
          countryName: currentCountryCodes.countryName,
          val: 1,
          currentCountryCodes: currentCountryCodes.currentCountryCodes,
        })
      } else if (supportedCountries.includes(curCountryCode))
        percentageRelatedArr.push({
          cc: curCountryCode,
          val: 1,
        })
    }
  })

  for (let h = 0; h < percentageRelatedArr.length; h++) {
    perc = Math.round((percentageRelatedArr[h].val / tree.length) * 100)
    perc = perc >= 1 ? perc : 1
    percentageRelatedArr[h].val = perc
  }

  return percentageRelatedArr
}

export function getNearestDescendantsValidLatLong(workingTree, ascendancyNumber, locationLookup) {
  // const isMale = ascendancyNumber % 2 === 0
  let childDPLL
  let ad

  ascendancyNumber = parseInt(ascendancyNumber, 10)
  do {
    ad = getChildAncestorLL(workingTree, ascendancyNumber, locationLookup)
    ascendancyNumber = ad.an
    childDPLL = ad.ll
  } while (ad.ll == null && ascendancyNumber > 0)

  if (ascendancyNumber === 0) {
    childDPLL = {
      latitude: 0,
      longitude: 0,
    }
  }
  return childDPLL
}

export function filterAncestorsByYears(tree, filteredTree, actionData) {
  const { years } = actionData
  filteredTree = []
  const startYear = parseInt(years[0], 10)
  const endYear = parseInt(years[1], 10)
  let lifeSpan = []
  let ancestorBirthYear
  let ancestorEndYear
  tree.forEach((element) => {
    lifeSpan = element.lifespan.split('-')
    ancestorBirthYear = parseInt(lifeSpan[0], 10)

    if (element.living) {
      ancestorEndYear = new Date().getFullYear()
    } else {
      ancestorEndYear = parseInt(lifeSpan[1], 10)
    }

    if (ancestorEndYear >= startYear && ancestorBirthYear <= endYear) {
      filteredTree.push(element)
    }
  })
  return filteredTree
}

export function updateMissingBirthLocation(tree, actionData) {
  const { latitude, longitude, placeDescription, placeId, personId } = actionData
  const ancestorIndex = tree.findIndex((person) => person.personId === personId)
  let ancestorToUpdate
  if (ancestorIndex !== -1) {
    ancestorToUpdate = tree.find((person) => person.personId === personId)
    const newLatLong = {
      latitude,
      longitude,
      name: placeDescription,
    }
    ancestorToUpdate.latLong = newLatLong
    if (ancestorToUpdate.facts && ancestorToUpdate.facts.length !== 0) {
      ancestorToUpdate.facts.forEach((fact) => {
        if (fact.type === 'http://gedcomx.org/Birth') {
          fact.placeDescription = placeId.toString()
          if (fact.display && 'placeFull' in fact.display) {
            fact.display.placeFull = placeDescription
          }
          if (fact.display && 'place' in fact.display) {
            fact.display.place = placeDescription
          }
        }
      })
    }
  }
  return ancestorToUpdate
}
